import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Mail, Pen } from 'lucide-react';
import axios from 'axios';
import BlogCard from '../components/BlogCard';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  useAnalytics('home');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts?limit=6`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const featuredPost = posts[0];
  const recentPosts = posts.slice(1, 4);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO url="/" />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1681018754447-abe57371b5de?crop=entropy&cs=srgb&fm=jpg&q=85')` 
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-20">
          <div className="max-w-2xl">
            <p className="text-sm tracking-widest uppercase text-amber-500 font-medium mb-6 animate-fade-in">
              Secret Stories from Nepal
            </p>
            <h1 className="font-serif text-5xl md:text-7xl text-stone-50 font-medium tracking-tight leading-[1.1] animate-fade-in animate-delay-100">
              Whispers of
              <br />
              <span className="text-amber-500">Nepal</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-stone-300 leading-relaxed max-w-lg font-light animate-fade-in animate-delay-200">
              Hidden stories from ancient temples, mountain trails, and 
              the untold secrets of Himalayan hospitality. A visual exploration.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in animate-delay-300">
              <Link
                to="/blog"
                className="bg-stone-50 text-stone-900 px-8 py-4 rounded-sm hover:bg-amber-500 hover:text-stone-900 transition-colors duration-300 font-medium tracking-wide text-sm uppercase flex items-center gap-2"
                data-testid="hero-explore-btn"
              >
                Explore Stories <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact#contact-info"
                className="bg-transparent border border-stone-50/30 text-stone-50 px-8 py-4 rounded-sm hover:border-stone-50 transition-all duration-300 text-sm tracking-wide uppercase"
                data-testid="hero-contact-btn"
              >
                Work With Me
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-stone-400 animate-bounce">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <ArrowDown size={20} />
        </div>
      </section>

      {/* Featured Story Section */}
      {featuredPost && (
        <section className="py-24 md:py-32 bg-stone-50" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-sm tracking-widest uppercase text-stone-500 font-medium">
                Featured Story
              </h2>
              <Link
                to="/blog"
                className="text-stone-900 hover:text-red-800 text-sm tracking-wide uppercase flex items-center gap-2 transition-colors"
              >
                All Stories <ArrowRight size={14} />
              </Link>
            </div>
            <BlogCard post={featuredPost} featured />
          </div>
        </section>
      )}

      {/* Recent Stories Grid */}
      {recentPosts.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="recent-stories-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <h2 className="text-sm tracking-widest uppercase text-stone-500 font-medium mb-12">
              Recent Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {recentPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Preview Section */}
      <section className="py-24 md:py-32 bg-stone-100" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-6">
                About the Author
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight mb-8">
                Stories Crafted with Purpose
              </h2>
              <p className="text-stone-600 leading-relaxed text-lg mb-6">
                I'm an aspiring travel journalist and photographer dedicated 
                to sharing authentic stories from Nepal and the greater Himalayan region.
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                I'm available for assignments, collaborations, and editorial partnerships.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-10 text-stone-900 font-medium hover:text-red-800 transition-colors"
                data-testid="about-preview-link"
              >
                Read My Story <ArrowRight size={16} />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="overflow-hidden rounded-sm">
                  <img
                    src="https://customer-assets.emergentagent.com/job_kathmandu-lens/artifacts/88wovhd8_martainnepal2.JPG"
                    alt="Marta Scharle - Travel Writer"
                    loading="lazy"
                    className="w-full aspect-square object-cover object-center filter saturate-[0.95] contrast-[1.02] brightness-[1.01]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-amber-500 rounded-sm -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <Mail size={40} className="text-amber-500 mx-auto mb-8" />
          <h2 className="font-serif text-4xl md:text-5xl text-stone-50 tracking-tight mb-6">
            Let's Create Together
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto mb-10">
            Looking for a travel journalist to cover your destination or story? 
            I'm available for assignments, collaborations, and editorial partnerships.
          </p>
          <Link
            to="/contact#contact-info"
            className="inline-flex items-center gap-2 bg-stone-50 text-stone-900 px-10 py-4 rounded-sm hover:bg-amber-500 transition-colors duration-300 font-medium tracking-wide text-sm uppercase"
            data-testid="cta-contact-btn"
          >
            Get in Touch <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <section className="py-24 md:py-32 bg-stone-50 pt-32" data-testid="empty-state">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
            <Pen size={48} className="text-stone-300 mx-auto mb-6" />
            <h2 className="font-serif text-3xl text-stone-900 mb-4">Stories Coming Soon</h2>
            <p className="text-stone-600 mb-8">
              The journey is just beginning. Check back soon for travel stories from Nepal.
            </p>
            <Link
              to="/contact#contact-info"
              className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-8 py-4 rounded-sm hover:bg-red-800 transition-colors"
            >
              Contact Me <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
